import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import { ArrowUpRight } from 'lucide-react';
import { serviceByBlogCategory } from '../../../data/site';
import { EASE_OUT } from '../../../lib/motion';

// Rough reading time from the Markdown body
export const readingTime = content => `${Math.max(1, Math.round((content || '').split(/\s+/).length / 200))} min read`;

/*
 * Editorial list of articles: category, title, reading time and an arrow per row.
 * Hovering a row slides a tint in from the left and nudges the title.
 * posts: [{ categoryId, slug, title, metaDescription, content }]
 */
const ArticleList = ({ posts, showDescription = false }) => {
  const reduced = useReducedMotion();

  return (
    <ul className="border-b border-border">
      {posts.map((post, i) => {
        const category = serviceByBlogCategory[post.categoryId];
        return (
          <motion.li
            key={`${post.categoryId}-${post.slug}`}
            initial={reduced ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease: EASE_OUT, delay: i * 0.06 }}
            className="border-t border-border"
          >
            <Link
              to={`/blog/${post.categoryId}/${post.slug}`}
              data-cursor="Read"
              className="group relative grid grid-cols-12 items-baseline gap-x-4 gap-y-2 overflow-hidden py-7 md:py-9"
            >
              <span aria-hidden="true" className="absolute inset-0 -z-10 origin-left scale-x-0 bg-foreground/[0.03] transition-transform duration-700 ease-out-expo group-hover:scale-x-100" />
              <span className="label col-span-12 md:col-span-3">{category?.label || 'Insight'}</span>
              <span className="col-span-11 md:col-span-8">
                <span className="title-lg block transition-transform duration-700 ease-out-expo group-hover:translate-x-2">{post.title}</span>
                {showDescription && <span className="mt-3 block max-w-2xl text-[0.9375rem] leading-relaxed text-muted-foreground">{post.metaDescription}</span>}
                <span className="label mt-3 block">{readingTime(post.content)}</span>
              </span>
              <ArrowUpRight
                size={22}
                strokeWidth={1.5}
                aria-hidden="true"
                className="col-span-1 justify-self-end text-muted-foreground transition-transform duration-500 ease-out-expo group-hover:rotate-45 group-hover:text-foreground"
              />
            </Link>
          </motion.li>
        );
      })}
    </ul>
  );
};

export default ArticleList;
